import {FC, useState} from 'react';
import {Box, Button, Stack, Typography} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';

import {
  Event,
  EventResult,
  EventStatus,
  formatEventResult,
  formatEventStatus,
  STATUS_TRANSITIONS,
  useUpdateEventStatus,
} from '@entities/event';
import {getErrorMessageFromApiError} from '@shared/api';
import {ObjectSelect} from '@shared/components';

interface Props {
  event: Event;
}

// Standalone "Resolution" card: status and result have their own endpoint
// (PATCH /events/{slug}/status), so they are applied together — independently
// of the main event update form — via this Apply button.
export const EventResolution: FC<Props> = ({event}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: updateEventStatus, isPending: isUpdatingStatus} =
    useUpdateEventStatus();
  const [status, setStatus] = useState<EventStatus>(event.status);
  const [result, setResult] = useState<EventResult>(event.result);

  const availableStatuses =
    STATUS_TRANSITIONS[event.status] ?? Object.values(EventStatus);
  const isResultEditable = status === EventStatus.closed;

  const handleApply = async () => {
    try {
      await updateEventStatus({eventSlug: event.eventId, status, result});
      enqueueSnackbar('Event status updated successfully', {
        variant: 'success',
      });
    } catch (error) {
      const msg = isAxiosError(error) ? getErrorMessageFromApiError(error) : '';
      enqueueSnackbar(`Could not update event status. ${msg}`, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Typography variant="h3bold" display="block" mb={4} mt={1}>
        Resolution
      </Typography>

      <Stack direction="row" spacing={4} maxWidth={620}>
        <ObjectSelect<{value: EventStatus; label: string}>
          label="Status"
          options={availableStatuses.map(s => ({
            value: s,
            label: formatEventStatus(s),
          }))}
          labelProp="label"
          valueProp="value"
          value={{value: status, label: formatEventStatus(status)}}
          onChange={opt => {
            if (!Array.isArray(opt) && opt !== null) setStatus(opt.value);
          }}
          fullWidth
        />

        <ObjectSelect<{value: EventResult; label: string}>
          label="Result"
          disabled={!isResultEditable}
          options={Object.values(EventResult).map(r => ({
            value: r,
            label: formatEventResult(r),
          }))}
          labelProp="label"
          valueProp="value"
          value={{value: result, label: formatEventResult(result)}}
          onChange={opt => {
            if (!Array.isArray(opt) && opt !== null) setResult(opt.value);
          }}
          fullWidth
        />
      </Stack>

      <Box mt={3} maxWidth={200}>
        <Button
          type="button"
          fullWidth
          onClick={() => {
            void handleApply();
          }}
          disabled={isUpdatingStatus}
        >
          Apply
        </Button>
      </Box>
    </>
  );
};
